/**
 * Server-side data fetching utilities for Neighborhood pages
 * This file uses EXISTING GraphQL functions from the modules
 *
 * Strategy:
 * 1. JSON first (SSR/SEO/fast initial render)
 * 2. Then GraphQL (real-time updates on client if needed)
 *
 * This file is for SERVER-SIDE data fetching only
 */

// Import existing GraphQL functions
import {
  getDetailsNhboundrieslist,
  getSchoolinNh,
  getKeywodSummary,
} from "../../../modules/Graphql/neighborhoodboundriesgraphql.js";
import { getAllquestions } from "../../../modules/Graphql/questionandanswergraphql.js";
import { getAllneighborhoodreview } from "../../../modules/Graphql/neighborhoodreviewgraphql.js";
import { getNeighborhubbyname } from "../../../modules/Graphql/neighborhubgraphql.js";
import { getcitydata } from "../../../modules/Graphql/cityquestionandanswergraphql.js";

/**
 * Fetch neighborhood details from GraphQL API
 * Uses the existing getDetailsNhboundrieslist function
 * @param {string} nhfullname - The neighborhood full name (slug)
 * @param {string} userId - User ID (optional, empty string for server-side)
 * @returns {Promise<Object>} Neighborhood details data
 */
export async function fetchNeighborhoodDetails(nhfullname, userId = "") {
  try {
    const getdata = {
      start: 0,
      limit: 1,
      order: "DESC",
      orderBy: "id",
      fields: [{ name: "nhfullname", value: nhfullname, operator: "=" }],
      userId: userId,
    };

    const result = await getDetailsNhboundrieslist(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching neighborhood details:", error);
    return null;
  }
}

/**
 * Fetch NPI ranking data for a city
 * Uses the existing getDetailsNhboundrieslist function
 */
export async function fetchNpiRanking(postalcity_name) {
  try {
    const getdata = {
      start: 0,
      limit: 300,
      order: "DESC",
      orderBy: "getcityranking",
      fields: [
        {
          name: "postalcity_name",
          value: postalcity_name,
          operator: "=",
        },
      ],
      userId: "",
    };

    const result = await getDetailsNhboundrieslist(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching NPI ranking:", error);
    return null;
  }
}

/**
 * Fetch keyword summary for a neighborhood
 * Uses the existing getKeywodSummary function
 */
export async function fetchKeywordSummary(neighborhoodId) {
  try {
    const getdata = {
      start: 0,
      limit: 10,
      orderBy: "id",
      order: "DESC",
      fields: [
        { name: "NeighborhoodID", value: neighborhoodId, operator: "=" },
      ],
    };

    const result = await getKeywodSummary(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching keyword summary:", error);
    return { data: { getKeywodSummary: [] } };
  }
}

/**
 * Fetch neighborhood reviews
 * Uses the existing getAllneighborhoodreview function
 */
export async function fetchNeighborhoodReviews(neighborhoodId) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      orderBy: "helpfulCount",
      order: "DESC",
      fields: [
        { name: "NeighborhoodID", value: neighborhoodId, operator: "=" },
      ],
    };

    const result = await getAllneighborhoodreview(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching neighborhood reviews:", error);
    return { data: { getneighborhoodreview: [] } };
  }
}

/**
 * Fetch neighborhood hubs by neighborhood name
 * Uses the existing getNeighborhubbyname function
 */
export async function fetchNeighborhoodHubByName(neighborhoodId) {
  try {
    const getdata = {
        start: 0,
        limit: 100,
        order: "DESC",
        orderBy: "get",
        fields: [
          {
            name: "neighborhood_name",
            value: neighborhoodId,
            operator: "=",
          },
        ],
      };

    const result = await getNeighborhubbyname(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching neighborhood reviews:", error);
    return { data: { nhHubData: [] } };
  }
}

/**
 * Fetch questions for a neighborhood
 * Uses the existing getAllquestions function
 */
export async function fetchQuestions(neighborhoodId) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      orderBy: "updatedDate",
      order: "DESC",
      fields: [
        { name: "neighborhoodId", value: neighborhoodId, operator: "=" },
      ],
    };

    const result = await getAllquestions(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching questions:", error);
    return { data: { getquestions: [] } };
  }
}

/**
 * Fetch school data for a neighborhood
 * Uses the existing getSchoolinNh function
 */
export async function fetchSchoolData(school_ids) {
  try {
    if (!school_ids || school_ids === "") {
      return { data: { getSchoolinNh: [] } };
    }

    const schoolidarr = school_ids.split(",");
    const schoolsids = schoolidarr.map((ids) => `'${ids}'`);
    const schoolidwithcomma = schoolsids.join(",");

    const getdata = {
      start: 0,
      limit: 10,
      orderBy: "instructionlevel",
      order: "DESC",
      fields: [{ name: "id", value: schoolidwithcomma, operator: "=" }],
      zoomLevel: 0,
    };

    const result = await getSchoolinNh(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching school data:", error);
    return { data: { getSchoolinNh: [] } };
  }
}

/**
 * Fetch city data (top neighborhoods)
 * Uses the existing getcitydata function
 */
export async function fetchCityData(cityfullname) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      order: "DESC",
      orderBy: "npi",
      fields: [{ name: "cityfullname", value: cityfullname, operator: "=" }],
    };

    const result = await getcitydata(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching city data:", error);
    return { data: { getcitydata: [] } };
  }
}

/**
 * Fetch all data for a neighborhood page (main aggregator function)
 * This is the MAIN function used by page.js
 */
export async function fetchAllNeighborhoodData(nhfullname) {
  try {
    console.log(`🔄 Fetching GraphQL data for: ${nhfullname}`);

    // First, fetch the main neighborhood details
    const nhDetailsData = await fetchNeighborhoodDetails(nhfullname);

    if (
      !nhDetailsData ||
      !nhDetailsData.data ||
      !nhDetailsData.data.getDetailsNhBoundries ||
      nhDetailsData.data.getDetailsNhBoundries.length === 0
    ) {
      console.log(`❌ No neighborhood data found for: ${nhfullname}`);
      return null;
    }

    const nhDetails = nhDetailsData?.data?.getDetailsNhBoundries?.[0];
    const neighborhoodId = nhDetails?.id;
    const postalCityName = nhDetails?.postalcity_name;
    const cityfullname = nhDetails?.cityfullname;
    const schoolIds = nhDetails?.schools_in_nh;

    // Fetch all related data in parallel for better performance
    const [
      npiRankingData,
      keywordSummaryData,
      reviewsData,
      schoolData,
      nhHubData,
      questionsData,
      // cityData,
    ] = await Promise.all([
      fetchNpiRanking(postalCityName),
      fetchKeywordSummary(neighborhoodId),
      fetchNeighborhoodReviews(neighborhoodId),
      fetchSchoolData(schoolIds),
      fetchNeighborhoodHubByName(nhfullname),
      fetchQuestions(neighborhoodId),
      // fetchCityData(cityfullname),
    ]);

    console.log(`✅ All GraphQL data fetched successfully for ${nhfullname}`);

    // Combine all data into a single result object
    // This matches the structure expected by NeighborhoodServer.js
    const result = {
      data: {
        getDetailsNhBoundries: nhDetailsData?.data?.getDetailsNhBoundries || [],
        getDetailsNhBoundriesnh:
          npiRankingData?.data?.getDetailsNhBoundries || [],
        getKeywodSummary: keywordSummaryData?.data?.getKeywodSummary || [],
        getneighborhoodreview: reviewsData?.data?.getneighborhoodreview || [],
        neighborhubData: nhHubData?.data?.getNeighborhub || [],
        getquestions: questionsData?.data?.getquestions || [],
        getSchoolinNh: schoolData?.data?.getSchoolinNh || [],
        // getcitydata: cityData?.data?.getcitydata || [],
      },
    };

    return { result };
  } catch (error) {
    console.error("❌ Error fetching all neighborhood data:", error);
    return null;
  }
}
